import { Icon, SxProps } from "@outschool/backpack";
import { faChevronDown, faPlus } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import { Overlay } from "@outschool/ui-legacy-component-library";
import React from "react";
import { Manager, Popper, Reference } from "react-popper";

import { useSearchByLearner } from "../../../providers/SearchByLearnerProvider";
import AddLearnerModal from "../../parentDashboardV2/components/AddLearnerModal";
import { useUpsertLearner } from "../../parentDashboardV2/hooks/useUpsertLearner";
import LearnerFilters from "./LearnerFilters";

// Match ToggleButton styling exactly (used by filter buttons like Age, Grade, etc.)
// ToggleButton has: padding 8px 18px, fontSize 14px, borderWidth 1px, lineHeight 1
// Backpack Button has 2px border (from SHARED_BUTTON_VARIANTS), so we need 7px vertical padding to match ToggleButton's effective height
// Must override both default AND hover border width since SHARED_BUTTON_VARIANTS forces 2px on hover
const BUTTON_SX: SxProps = {
  paddingTop: "7px",
  paddingBottom: "7px",
  paddingLeft: "18px",
  paddingRight: "18px",
  fontSize: "14px",
  lineHeight: 1,
  borderWidth: "1px",
  "&:hover:not(:disabled), &:focus:not(:disabled), &:active:not(:disabled)": {
    borderWidth: "1px",
  },
};

/**
 * Learner filter dropdown component for desktop search.
 */
export function LearnerFilterDropdown({
  openFilterDropdown,
  closeFilterDropdown,
  setOpenFilterDropdown,
}: {
  openFilterDropdown: string | null;
  closeFilterDropdown: () => void;
  setOpenFilterDropdown: (value: "learner" | null) => void;
}) {
  const { t } = useTranslation(
    "client\\components\\search\\LearnerFilterDropdown",
  );
  const { currentUser } = useSession();
  const { learner, setLearner } = useSearchByLearner();

  const hasLearners = currentUser?.children && currentUser.children.length > 0;

  const { openModal, upsertChild, ...addLearnerModalProps } =
    useUpsertLearner();

  const handleAddLearner = async () => {
    const newLearner = await upsertChild();
    if (newLearner) {
      setLearner(newLearner);
    }
    return newLearner;
  };

  // Treatment & holdout: logged in but no learners - show "Add a learner" button
  if (!hasLearners) {
    return (
      <>
        <AddLearnerModal
          {...addLearnerModalProps}
          upsertChild={handleAddLearner}
        />
        <TrackedButton
          variant="contained"
          color="primary"
          size="small"
          onClick={openModal}
          startIcon={<Icon icon={faPlus} />}
          sx={BUTTON_SX}
          data-test-id="learner-filter-add-learner-button"
          trackingName="learner_filter_add_learner_button"
          trackingSharedProperties={{
            location: "desktop_search",
            has_learners: false,
          }}
        >
          {t("Add a learner")}
        </TrackedButton>
      </>
    );
  }

  // Logged in with learners - show filter dropdown
  const isOpen = openFilterDropdown === "learner";
  const displayName = learner ? learner.name ?? t("All") : t("All");

  const handleClick = () => {
    if (isOpen) {
      closeFilterDropdown();
    } else {
      setOpenFilterDropdown("learner");
    }
  };

  return (
    <Manager>
      <div>
        <Reference>
          {({ ref }) => (
            <div ref={ref}>
              <TrackedButton
                variant="contained"
                color="primary"
                size="small"
                onClick={handleClick}
                endIcon={<Icon icon={faChevronDown} />}
                sx={BUTTON_SX}
                data-test-id="learner-filter-button"
                trackingName="learner_filter_dropdown_toggle"
                trackingSharedProperties={{
                  location: "desktop_search",
                  selected_learner: learner?.uid ?? "all",
                  is_open: isOpen,
                }}
              >
                {displayName}
              </TrackedButton>
            </div>
          )}
        </Reference>

        {isOpen && (
          <Popper
            placement="bottom-start"
            positionFixed
            modifiers={{
              preventOverflow: {
                padding: 0,
                boundariesElement: "viewport",
              },
              flip: {
                enabled: false,
              },
              scroll: {
                enabled: true,
                fn(data) {
                  data.styles.overflowY = "auto";
                  return data;
                },
              },
            }}
          >
            {({ ref, style }) => {
              return (
                <Overlay
                  ref={ref}
                  sx={{
                    marginTop: "0.5em",
                    padding: "0.5em",
                  }}
                  style={style}
                  data-test-id="learner-filter-button-menu"
                >
                  <LearnerFilters />
                </Overlay>
              );
            }}
          </Popper>
        )}
      </div>
    </Manager>
  );
}
